
import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #updateVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#updateVersion += 1;
    this.#controller.abort();
  }

  /**
   * Get media IDs in slideshow order. The gallery may also contain a desktop
   * grid, so read the slideshow first to avoid counting each image twice.
   *
   * @param {Element} gallery
   * @returns {string[]}
   */
  #getMediaIds(gallery) {
    const slides = gallery.querySelectorAll('slideshow-slide .product-media[data-media-id]');
    const images = slides.length
      ? slides
      : gallery.querySelectorAll('.media-gallery__grid .product-media[data-media-id]');

    return Array.from(images, (media) => media.getAttribute('data-media-id') || '');
  }

  /**
   * Preload the next first image only when a complete gallery replacement is
   * actually necessary. Leave the old gallery visible while it loads.
   *
   * @param {Element} gallery
   */
  async #preloadFirstImage(gallery) {
    const image = /** @type {HTMLImageElement | null} */ (
      gallery.querySelector('slideshow-slide img, .media-gallery__grid img')
    );
    if (!image) return;

    const src = image.getAttribute('src');
    if (!src) return;

    await Promise.race([
      new Promise((resolve) => {
        const preload = new Image();
        const srcset = image.getAttribute('srcset');
        const sizes = image.getAttribute('sizes');
        if (srcset) preload.srcset = srcset;
        if (sizes) preload.sizes = sizes;
        preload.onload = resolve;
        preload.onerror = resolve;
        preload.src = src;
        if (preload.complete) resolve();
      }),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  }

  /**
   * Avoid rebuilding the media gallery when a size change uses the same
   * images. If a different featured image already exists in the carousel,
   * select that image without removing the whole gallery.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const updateVersion = ++this.#updateVersion;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html || updateVersion !== this.#updateVersion || !this.isConnected) return;

        const { html, newProduct } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const currentIds = this.#getMediaIds(this);
        const nextIds = this.#getMediaIds(newMediaGallery);
        const knownMedia = currentIds.length > 0 && nextIds.length > 0;
        const sameOrder =
          knownMedia &&
          currentIds.length === nextIds.length &&
          currentIds.every((id, index) => id === nextIds[index]);

        // No media difference: keep the loaded images, zoom and slideshow.
        if (!newProduct && sameOrder) return;

        const sameSet =
          knownMedia &&
          currentIds.length === nextIds.length &&
          [...currentIds].sort().join('|') === [...nextIds].sort().join('|');

        // Horizon puts the selected variant's featured media first in the
        // server HTML. If the media set is unchanged, select it in the
        // existing carousel rather than rebuilding all the images.
        if (!newProduct && sameSet && this.presentation === 'carousel' && this.slideshow) {
          const featuredIndex = currentIds.indexOf(nextIds[0]);
          if (featuredIndex >= 0) {
            this.slideshow.select(featuredIndex, undefined, { animate: false });
            return;
          }
        }

        // Different media set, desktop grid order, or a new product:
        // a replacement may be needed. Preload to reduce the blank frame.
        await this.#preloadFirstImage(newMediaGallery);

        // Ignore an outdated response if sizes were changed rapidly.
        if (updateVersion !== this.#updateVersion || !this.isConnected) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
